import { useEffect, useState } from "react";
import { Check, X, Loader2 } from "lucide-react";
import { api, formatApiErrorDetail } from "../../../lib/api";
import useSettings from "./useSettings";
import { SettingsHeader, Section, Loading } from "./_shared";

export default function NewsletterSettingsAdmin() {
  const { settings, set, save, saving, saved } = useSettings();
  const [probing, setProbing] = useState(false);
  const [probe, setProbe] = useState(null);
  const [lists, setLists] = useState(null);
  const [loadingLists, setLoadingLists] = useState(false);
  const [listsError, setListsError] = useState("");

  useEffect(() => {
    if (settings && (settings.acumbamail_auth_token || "").trim() && lists === null) {
      api.post("/admin/newsletter/lists", { auth_token: settings.acumbamail_auth_token })
        .then((resp) => setLists(resp.data.lists || []))
        .catch(() => { /* silent — the manual "Cargar listas" button will handle retries */ });
    }
  }, [settings, lists]);

  if (!settings) return <Loading />;

  const testConnection = async () => {
    setProbing(true);
    setProbe(null);
    try {
      await api.put("/admin/settings", settings);
      const { data } = await api.post("/admin/newsletter/test-connection");
      setProbe({ ok: data.list_id_found, msg: data.message });
    } catch (e) {
      setProbe({ ok: false, msg: formatApiErrorDetail(e.response?.data?.detail) || e.message });
    } finally {
      setProbing(false);
    }
  };

  const loadLists = async () => {
    setListsError("");
    setLoadingLists(true);
    try {
      const { data } = await api.post("/admin/newsletter/lists", { auth_token: settings.acumbamail_auth_token });
      setLists(data.lists || []);
    } catch (e) {
      setLists(null);
      setListsError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setLoadingLists(false);
    }
  };

  return (
    <div data-testid="cms-settings-newsletter">
      <SettingsHeader
        title="Newsletter — Acumbamail"
        description="Conecte el formulario de suscripción con su cuenta de Acumbamail."
        onSave={save}
        saving={saving}
        saved={saved}
      />

      <Section title="Configuración Acumbamail">
        <p className="text-xs text-gray-500 mb-4">
          Necesita su <strong>auth_token</strong> y el <strong>list_id</strong> numérico de la lista donde añadir los suscriptores.
          Obtenga estos valores en <a href="https://acumbamail.com/login/?next=/apidoc" target="_blank" rel="noreferrer" className="text-[#C8102E] hover:underline">acumbamail.com/apidoc</a> una vez conectado.
        </p>
        <div className="grid md:grid-cols-2 gap-4 mb-4">
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1 uppercase tracking-wider">Auth token</label>
            <input
              value={settings.acumbamail_auth_token || ""}
              onChange={(e) => { set("acumbamail_auth_token", e.target.value); setLists(null); setProbe(null); }}
              className="w-full px-3 py-2 border border-gray-200 focus:border-[#0F2040] outline-none text-sm font-mono"
              data-testid="settings-acumba-token"
              placeholder="Pegue aquí su auth_token"
            />
          </div>
          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="block text-xs font-medium text-gray-700 uppercase tracking-wider">Lista de destino</label>
              <button
                type="button"
                onClick={loadLists}
                disabled={loadingLists || !settings.acumbamail_auth_token}
                data-testid="settings-load-lists"
                className="text-[11px] text-[#C8102E] hover:underline disabled:text-gray-400 disabled:no-underline inline-flex items-center gap-1"
              >
                {loadingLists ? <Loader2 className="w-3 h-3 animate-spin" /> : null}
                {lists === null ? "Cargar listas" : "Actualizar listas"}
              </button>
            </div>
            {lists && lists.length > 0 ? (
              <select
                value={settings.acumbamail_list_id || ""}
                onChange={(e) => set("acumbamail_list_id", e.target.value)}
                className="w-full px-3 py-2 border border-gray-200 focus:border-[#0F2040] outline-none text-sm bg-white"
                data-testid="settings-acumba-list-select"
              >
                <option value="">— Seleccione una lista —</option>
                {lists.map((l) => (
                  <option key={l.id} value={l.id}>
                    {`${l.name}${l.subscribers ? ` · ${l.subscribers} suscriptores` : ""} (ID ${l.id})`}
                  </option>
                ))}
              </select>
            ) : lists && lists.length === 0 ? (
              <div className="text-xs text-gray-500 border border-gray-200 px-3 py-2">
                No hay listas en su cuenta. Créela primero desde Acumbamail.
              </div>
            ) : (
              <input
                value={settings.acumbamail_list_id || ""}
                onChange={(e) => set("acumbamail_list_id", e.target.value)}
                className="w-full px-3 py-2 border border-gray-200 focus:border-[#0F2040] outline-none text-sm"
                placeholder="Pulse «Cargar listas» o escriba el ID manualmente"
                data-testid="settings-acumba-list-id"
              />
            )}
            {listsError && <p className="text-xs text-red-600 mt-1">{listsError}</p>}
            {lists && settings.acumbamail_list_id &&
              !lists.find((l) => l.id === String(settings.acumbamail_list_id)) && (
              <p className="text-xs text-amber-600 mt-1">
                El ID guardado ({settings.acumbamail_list_id}) no aparece en las listas cargadas.
              </p>
            )}
          </div>
        </div>
        <label className="flex items-center gap-2 text-sm mb-4">
          <input
            type="checkbox"
            checked={!!settings.acumbamail_double_optin}
            onChange={(e) => set("acumbamail_double_optin", e.target.checked)}
            className="accent-[#C8102E]"
            data-testid="settings-double-optin"
          />
          <span>
            Activar <strong>double opt-in</strong> (Acumbamail envía un email de confirmación al nuevo suscriptor)
          </span>
        </label>
        <div className="flex items-center gap-3 flex-wrap">
          <button
            type="button"
            onClick={testConnection}
            disabled={probing || !settings.acumbamail_auth_token || !settings.acumbamail_list_id}
            data-testid="settings-test-acumba"
            className="inline-flex items-center gap-2 px-4 py-2 text-sm border border-[#0F2040] text-[#0F2040] hover:bg-[#0F2040] hover:text-white transition-colors disabled:opacity-40"
          >
            {probing ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
            Probar conexión
          </button>
          {probe && (
            <span className={`text-sm inline-flex items-center gap-1.5 ${probe.ok ? "text-green-700" : "text-red-700"}`} data-testid="settings-probe-result">
              {probe.ok ? <Check className="w-4 h-4" /> : <X className="w-4 h-4" />}
              {probe.msg}
            </span>
          )}
        </div>
      </Section>
    </div>
  );
}
